/* @flow */
import type {Page, Book, Chapter, Options} from '../flow/book'
import bookService from '../services/book'
import offlineHandler, { OfflineHandler } from '../services/fs'

export default class BaseRenderer {
  bookId: string
  title: string
  pages: Array<Page>
  currentPageNumber: number
  secondPageNumber: ?number
  dynamicEnrichments: ?Object
  twoPages: boolean
  offline: OfflineHandler
  options: ?Object

  constructor (book: Book, offline: OfflineHandler = offlineHandler, options: Options = {page: 1}) {
    this.bookId = book._id
    this.title = book.title
    this.pages = book.content
    this.options = options
    this.currentPageNumber = options.page
    this.secondPageNumber = null
    this.twoPages = true
    this.dynamicEnrichments = {}
    this.offline = offline

    if (book.dynamic_enrichment) {
      this.setDynamicEnrichmentsByPage(book.dynamic_enrichment)
    }
  }

  getTotalPages (): number {
    return this.pages.length
  }

  getPage (pageNumber: number): Page {
    const totalPages: number = this.getTotalPages()

    pageNumber = parseInt(pageNumber)

    pageNumber = pageNumber >= 1 ? pageNumber : 1
    pageNumber = pageNumber > totalPages ? totalPages : pageNumber

    if (this.twoPages && pageNumber % 2 === 1 && this.currentPageNumber > 1) {
      this.secondPageNumber = pageNumber
      this.currentPageNumber = pageNumber - 1

      return this.pages[pageNumber - 1]
    }

    this.currentPageNumber = pageNumber

    return this.pages[pageNumber - 1]
  }

  getCurrentPage (): Page {
    return this.getPage(this.currentPageNumber)
  }

  setDynamicEnrichmentsByPage (dynamicEntrichments): void {
    const deObj = JSON.parse(dynamicEntrichments)
    const keysToArray = Object.keys(deObj)

    keysToArray.forEach(key => {
      this.dynamicEnrichments[key.replace( /[^\d]/g, '')] = deObj[key]
    })
  }

  hasDynamicEnrichments (page): Boolean {
    return this.dynamicEnrichments[page]
  }

  getDynamicEnrichmentsPageUrl (page): Object {
    return this.pages[page - 1].extraURL
  }

  gotoPage (pageNumber: number): Page | void {
    return this.getPage(pageNumber)
  }

  getPreviousPage (): Page | void {
    return this.getPage(this.twoPages ? this.currentPageNumber - 2 : this.currentPageNumber - 1)
  }

  getNextPage (): Page | void {
    return this.getPage(this.twoPages && this.currentPageNumber > 1 ? this.currentPageNumber + 2 : this.currentPageNumber + 1)
  }

  getTableOfContent (): Promise<Array<string | Chapter>> {
    return bookService.fetchTableOfContent(this.bookId)
  }

  async pageChangeHandler (): Promise<Array<any>> {
    let arrPages: Array<any> = []
    let page: Promise<string> = await this.fetchPage(this.getPage(this.currentPageNumber))

    arrPages.push(page)

    if (this.twoPages && this.currentPageNumber !== 1 && this.currentPageNumber !== this.getTotalPages()) {
      let page2: Promise<string> = await this.fetchPage(this.getPage(this.currentPageNumber + 1))

      arrPages.push(page2)
    }

    return arrPages
  }

  async fetchPage (page: Page): Promise<string> {
    return bookService
      .fetchPage(this.bookId, page.name)
      .catch(async () => {
        return this.offline.getFileAsText(`/books/${this.bookId}/${page.name}`)
      })
  }

  searchInBook (term: string): Promise<Array<{}>> {
    return bookService.searchInBook(this.bookId, term)
  }
}
